import React from 'react';
import { ui } from '../../config/uiSizes';
import { useFindingsDisclosure } from './FindingsDisclosureContext';

type FindingsVariant = 'amber' | 'yellow' | 'neutral';

interface CollapsibleFindingsSectionProps {
    /** Heading shown in the always-visible summary row. */
    title: string;
    /** SVG path `d` for the leading status icon (rendered at `ui.svg.sm`). Omit for no icon. */
    iconPath?: string;
    /** Colour scheme — `amber` for readiness/reconciliation, `yellow` for warnings, `neutral` for content. */
    variant?: FindingsVariant;
    /** Item count shown as a neutral badge next to the title. Hidden when undefined. */
    count?: number;
    /** Optional critical badge (e.g. a red duplicate-name count) kept visible while collapsed. */
    criticalBadge?: React.ReactNode;
    /** Optional inline element rendered next to the title (e.g. a help tooltip). */
    titleAccessory?: React.ReactNode;
    /** One-line gist that stays visible while collapsed so nothing important is fully hidden. */
    summary?: React.ReactNode;
    /** Stable id used (with the provider's prefix) to persist open state in localStorage. */
    sectionId?: string;
    /** Initial open state when there is no persisted value. Defaults to collapsed. */
    defaultOpen?: boolean;
    /** Expanded body — the full detail list and any guidance. */
    children: React.ReactNode;
    /** Optional extra classes for the outer container. */
    className?: string;
}

const VARIANT_STYLES: Record<FindingsVariant, {
    container: string;
    icon: string;
    title: string;
    summary: string;
    badge: string;
    chevron: string;
}> = {
    amber: {
        container: 'bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700',
        icon: 'text-amber-600 dark:text-amber-400',
        title: 'text-amber-800 dark:text-amber-200',
        summary: 'text-amber-800 dark:text-amber-200',
        badge: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
        chevron: 'text-amber-500 dark:text-amber-400',
    },
    yellow: {
        container: 'bg-yellow-50 dark:bg-yellow-700/30 border-yellow-300 dark:border-yellow-600',
        icon: 'text-yellow-600 dark:text-yellow-300',
        title: 'text-yellow-800 dark:text-yellow-200',
        summary: 'text-yellow-800 dark:text-yellow-200',
        badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-800/50 dark:text-yellow-100',
        chevron: 'text-yellow-500 dark:text-yellow-300',
    },
    neutral: {
        container: 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700',
        icon: 'text-gray-500 dark:text-gray-400',
        title: 'text-gray-900 dark:text-gray-100',
        summary: 'text-gray-500 dark:text-gray-400',
        badge: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
        chevron: 'text-gray-400 dark:text-gray-500',
    },
};

/** Reads a persisted open state; returns null when unset or unavailable. */
function readStoredOpen(key: string): boolean | null {
    try {
        const value = window.localStorage.getItem(key);
        return value === null ? null : value === '1';
    } catch {
        return null;
    }
}

/** Persists an open state, ignoring storage failures (private mode, quota, SSR). */
function writeStoredOpen(key: string, open: boolean): void {
    try {
        window.localStorage.setItem(key, open ? '1' : '0');
    } catch {
        /* storage unavailable — persistence is best-effort */
    }
}

/**
 * Accessible progressive-disclosure wrapper for the migration-attention findings panels
 * ("Not fully migrated", "Target org readiness", division warnings) and content sections
 * (selected resources, history). Built on the native `<details>`/`<summary>` element so it is
 * keyboard- and screen-reader-accessible for free and matches the disclosure pattern used elsewhere.
 *
 * The summary row (icon, title, count badge, optional critical badge, and a one-line gist) stays
 * visible while collapsed, so severity is never hidden behind a click; only the long detail list
 * collapses. Open state is controlled so it can respond to a `FindingsDisclosureProvider`'s
 * expand/collapse-all commands and persist per section in localStorage across reopenings.
 */
const CollapsibleFindingsSection: React.FC<CollapsibleFindingsSectionProps> = ({
    title,
    iconPath,
    variant = 'amber',
    count,
    criticalBadge,
    titleAccessory,
    summary,
    sectionId,
    defaultOpen = false,
    children,
    className = '',
}) => {
    const styles = VARIANT_STYLES[variant];
    const disclosure = useFindingsDisclosure();
    const storageKey = disclosure && sectionId ? `${disclosure.storagePrefix}:${sectionId}` : null;

    const [open, setOpen] = React.useState<boolean>(() => {
        if (storageKey) {
            const stored = readStoredOpen(storageKey);
            if (stored !== null) return stored;
        }
        return defaultOpen;
    });

    const applyOpen = React.useCallback(
        (next: boolean) => {
            setOpen(next);
            if (storageKey) writeStoredOpen(storageKey, next);
        },
        [storageKey],
    );

    // Apply each expand/collapse-all command exactly once.
    const lastSeq = React.useRef<number | null>(null);
    React.useEffect(() => {
        const command = disclosure?.command;
        if (command && command.seq !== lastSeq.current) {
            lastSeq.current = command.seq;
            applyOpen(command.open);
        }
    }, [disclosure?.command, applyOpen]);

    return (
        <details
            className={`group border rounded-lg ${styles.container} ${ui.page.sectionPadding} ${ui.mb.title} ${className}`}
            open={open}
            onToggle={(e) => applyOpen((e.currentTarget as HTMLDetailsElement).open)}
        >
            <summary
                className={`flex items-start ${ui.gap.sm} cursor-pointer list-none [&::-webkit-details-marker]:hidden`}
            >
                {iconPath && (
                    <svg
                        className={`${ui.svg.sm} ${styles.icon} shrink-0 mt-0.5`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                    </svg>
                )}

                <div className="flex-1 min-w-0">
                    <div className={`flex items-center flex-wrap ${ui.gap.sm}`}>
                        <span className={`${ui.text.label} font-bold ${styles.title}`}>{title}</span>
                        {titleAccessory}
                        {count !== undefined && (
                            <span
                                className={`inline-flex items-center ${ui.badge.padding} rounded-full ${ui.text.badge} font-semibold ${styles.badge}`}
                            >
                                {count}
                            </span>
                        )}
                        {criticalBadge}
                    </div>
                    {summary && (
                        <p className={`${ui.text.xs} ${styles.summary} mt-0.5 break-words`}>{summary}</p>
                    )}
                </div>

                <svg
                    className={`w-4 h-4 shrink-0 ${styles.chevron} transition-transform group-open:rotate-90`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                >
                    <path
                        fillRule="evenodd"
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                    />
                </svg>
            </summary>

            <div className={ui.mt.md}>{children}</div>
        </details>
    );
};

export default CollapsibleFindingsSection;
